import { useCallback, useEffect, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { useNavigate, useLocation } from 'react-router'
import classNames from 'classnames'
import 'react-perfect-scrollbar/dist/css/styles.css'

// evertel
import { Icon, Col, Badge, Row, MenuHeader, Button } from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { SessionService } from '@evertel/session/SessionService'
import { DepartmentAvatar } from '@evertel/web/department'
import { CurrentUserDepartmentsController, UnreadCountsState } from '@evertel/blue-user'
import { DrawerController } from '@evertel/drawer'
import { FloatingMenu, MenuItem } from '@evertel/web/ui'
import { LeftGutterAppButton } from './elements'


const LeftGutter = observer(() => {

    const sessionService = useService(SessionService, [])
    const drawerController = useService(DrawerController, [])
    const currentUserDepartmentsController = useService(CurrentUserDepartmentsController, [])
    const unreadState = useService(UnreadCountsState, [])

    const { roomUnreadCountsByDept, hasRoomUnreadUrgentsByDept, threadsMessageUnreadCount, hasThreadUrgents } = unreadState

    const totalUnreads = Object.values(roomUnreadCountsByDept).reduce((acc, val) => acc + val, 0) + threadsMessageUnreadCount

    const navigate = useNavigate()
    const location = useLocation()

    const onSwitchDepartment = (deptId: number) => {
        // set the new department as selected
        sessionService.setSelectedDeparmentId(deptId)

        // get last path in that dept.
        const lastPath = sessionService.previousPath

        if (['/admin', '/manage'].some(path => location.pathname.includes(path))) {
            return
        }

        if (lastPath) {
            // go to last path
            navigate(lastPath)
        } else {
            // if not last path, take them to default
            navigate('/landing')
        }
    }

    const getMessagePath = useCallback(() => {
        const pathname = location.pathname

        //if the user is already in the message section, then this should take them to landing
        const onRoomOrThread = pathname.includes('/room/') || pathname.includes('/thread/')
        if (onRoomOrThread) {
            return '/landing'
        }

        //if not it should go to the previous place they were in message section
        return sessionService.previousRoomOrThread?.path || '/landing'

    }, [location, sessionService.previousRoomOrThread])

    const maxDisplayOtherDepts = 3

    const recentDepartmentIds = sessionService.recentDepartmentIds

    //the extra map is to get rid of errors about id maybe being undefined
    const otherDepts = (currentUserDepartmentsController.departmentsNotGuestVerified?.filter(d => d.id !== sessionService.selectedDepartmentId) || []).map(d => ({ ...d, id: d.id as number, name: d.name as string }))

    const guestDepts = currentUserDepartmentsController.departmentsGuestVerified?.filter(d => d.id !== sessionService.selectedDepartmentId)

    // Sort both department lists using the controller's sorting method
    const sortedDepts = useMemo(() => {
        return currentUserDepartmentsController.getSortedDepartments(
            otherDepts,
            recentDepartmentIds,
            roomUnreadCountsByDept,
            hasRoomUnreadUrgentsByDept
        )
    }, [otherDepts, recentDepartmentIds, roomUnreadCountsByDept, hasRoomUnreadUrgentsByDept])

    const sortedGuestDepts = useMemo(() => {
        return currentUserDepartmentsController.getSortedDepartments(
            guestDepts || [],
            recentDepartmentIds,
            roomUnreadCountsByDept,
            hasRoomUnreadUrgentsByDept
        )
    }, [guestDepts, recentDepartmentIds, roomUnreadCountsByDept, hasRoomUnreadUrgentsByDept])

    const currentTotalCount = threadsMessageUnreadCount + (roomUnreadCountsByDept[sessionService?.selectedDepartmentId] || 0)
    const currentTotalHasUrgent = hasThreadUrgents || hasRoomUnreadUrgentsByDept[sessionService?.selectedDepartmentId] || false

    // Check if user has no verified departments available
    const hasNoDepartments = !sessionService.selectedDepartmentId && sortedDepts.length === 0 && sortedGuestDepts.length === 0
    const extraDepartmentsCount = (sortedDepts?.length ?? 0) + (sortedGuestDepts?.length ?? 0)

    return (
        <Col
            data-bs-theme="dark"
            className="left-gutter">
            <PerfectScrollbar options={{ suppressScrollX: true }} style={{ textAlign: 'center' }}>
                {hasNoDepartments ? (
                    <Col align="center" className="dept-icons-wrapper no-dept mx-3 my-2">
                        <Button
                            color="transparent"
                            onClick={() => navigate('/account/departments/join')}
                            className="d-flex align-items-center justify-content-center m-1"
                            style={{
                                width: 35,
                                height: 35,
                                borderColor: 'transparent',
                                borderRadius: '50%'
                            }}
                        >
                            <Icon
                                name="circle-plus"
                                color="primary"
                                type="solid"
                                size={25}
                            />
                        </Button>
                    </Col>
                ) : (
                    <FloatingMenu
                        label="Agencies"
                        renderLabel={() => (
                            <>
                                <Col
                                    align="center"
                                    className="dept-icons-wrapper">
                                    { sessionService.selectedDepartmentId ? (
                                        <DepartmentAvatar
                                            departmentId={sessionService.selectedDepartmentId as number}
                                            width={35}
                                            height={35}
                                            className={classNames('active', {'mb-4': extraDepartmentsCount})}
                                        />
                                    ) : (
                                        <Icon
                                            name="building-user"
                                            color="primary"
                                            type="light"
                                            size={30}
                                            className="mb-4 active"
                                        />
                                    )}
                                    {/* grab a max of 3 other depts */}
                                    {[...sortedDepts, ...sortedGuestDepts]?.slice(0, maxDisplayOtherDepts)?.map((dept) =>
                                        <Row key={dept.id} className="position-relative">
                                            <DepartmentAvatar
                                                key={dept.id}
                                                departmentId={dept.id as number}
                                                width={25}
                                                height={25}
                                                className="mb-2"
                                            />
                                            {(roomUnreadCountsByDept && roomUnreadCountsByDept[dept.id] > 0) &&
                                                <Badge
                                                    color={hasRoomUnreadUrgentsByDept[dept.id] ? 'danger' : 'warning'}
                                                    size={10}
                                                    shape="rounded-pill"
                                                    className="position-absolute translate-middle"
                                                >
                                                    {(roomUnreadCountsByDept[dept.id] > 200) ? '200+' : roomUnreadCountsByDept[dept.id]}
                                                </Badge>
                                            }
                                        </Row>
                                    )}
                                </Col>
                                {(sortedDepts?.length + sortedGuestDepts?.length) > maxDisplayOtherDepts &&
                                    <Icon
                                        name="caret-down"
                                        color="white"
                                        type="solid"
                                        size={16}
                                        style={{ width: 25 }}
                                    />
                                }
                            </>
                        )}
                    >
                        <PerfectScrollbar options={{ suppressScrollX: true }}>
                            {sortedDepts?.map((dept) => (
                                <MenuItem
                                    key={dept.id}
                                    label={dept.name || ''}
                                    onClick={() => onSwitchDepartment(dept.id as number)}>
                                    <DepartmentAvatar
                                        departmentId={dept.id as number}
                                        width={25}
                                        height={25}
                                        className="me-2"
                                    />
                                    <div className="ms-2">
                                        {dept.name}
                                    </div>
                                    {(roomUnreadCountsByDept && roomUnreadCountsByDept[dept.id] > 0) &&
                                        <Badge
                                            color={hasRoomUnreadUrgentsByDept[dept.id] ? 'danger' : 'warning'}
                                            className="ms-2"
                                        >
                                            {roomUnreadCountsByDept[dept.id]}
                                        </Badge>
                                    }
                                </MenuItem>
                            ))}

                            {sortedGuestDepts && sortedGuestDepts.length > 0 && (
                                <>
                                    <MenuHeader label="Agencies I'm a guest in" className="pt-2 pb-2"/>
                                    {sortedGuestDepts?.map((dept) => (
                                        <MenuItem
                                            key={dept.id}
                                            label={dept.name || ''}
                                            onClick={() => onSwitchDepartment(dept.id as number)}>
                                            <DepartmentAvatar
                                                departmentId={dept.id as number}
                                                width={25}
                                                height={25}
                                                className="me-2"
                                            />
                                            <div className="ms-2">
                                                {dept.name}
                                            </div>
                                            {(roomUnreadCountsByDept && roomUnreadCountsByDept[dept.id] > 0) &&
                                            <Badge
                                                color={hasRoomUnreadUrgentsByDept[dept.id] ? 'danger' : 'warning'}
                                                className="ms-2"
                                            >
                                                {roomUnreadCountsByDept[dept.id]}
                                            </Badge>
                                            }
                                        </MenuItem>
                                    ))}
                                </>
                            )}
                        </PerfectScrollbar>
                        <MenuItem
                            style={{ borderTop: '2px solid var(--bs-border-color)' }}
                            icon={{ name: 'plus-circle', size: 18, className: 'me-2', style: { width: 25, textAlign: 'center' } }}
                            label="Manage My Agencies"
                            to="/account/departments"
                        />
                    </FloatingMenu>
                )}

                <LeftGutterAppButton
                    to={getMessagePath()}
                    icon="message"
                    active={drawerController.activeSection === 'messages'}
                    label="Messages"
                    badgeCount={currentTotalCount}
                    hasUrgents={currentTotalHasUrgent}
                />

                <LeftGutterAppButton
                    to={`/everdocs`}
                    icon="file-alt"
                    label="EverDocs"
                    active={drawerController.activeSection === 'everdocs'}
                />
            </PerfectScrollbar>
        </Col>
    )
})

export { LeftGutter }
